import { showToast } from "@scayla/components";
import type { Ref } from "vue";
import { isRef, watch } from "vue";

import { $t } from "@/i18n";

export interface BaseError {
  message: string;
  detail: string;
  errorCode?: string;
  statusCode?: number;
}

/**
 * Options for the toast helpers.
 */
export interface ToastOptions {
  /** Custom handler for Scayla API errors.
      @returns A custom error message, `false` to ignore the error, `true` to use the default handler (display `.message`)
   */
  handleScaylaError?: (error: BaseError) => string | boolean;
}

function resolveMessage(message: Ref<string> | string): string | null {
  if (isRef(message)) {
    return message.value || null;
  }
  return message || null;
}

/**
 * All four helpers return `void` on purpose: they are routinely passed straight to TanStack's
 * `onError`/`onSuccess`, whose callbacks must return `void | Promise<void>`. Returning the
 * snackbar handle instead makes every such call site a type error, and nothing consumes it.
 */
export const showToastForError = (error: unknown, options?: ToastOptions): void => {
  if (!error) {
    return;
  }
  if (isRef(error) && error.value === null) {
    return;
  }

  // ignore intentional abort errors from cancelled requests
  if (typeof error === "object" && "name" in error && error.name === "AbortError") {
    return;
  }

  let text = $t("common.label.unknownError");
  if (typeof error === "string") {
    text = error;
  } else if (
    typeof error === "object" &&
    "statusCode" in error &&
    error.statusCode === 423 &&
    "errorCode" in error &&
    error.errorCode === "DeleteRestrictError"
  ) {
    if ("resource" in error) {
      text = $t("common.info.resourceLookingDelete", {
        Resource: error.resource as string,
      });
    } else {
      text = $t("common.label.resourceLookingDelete");
    }
  } else if (typeof error === "object" && "message" in error) {
    const customScaylaHandlerRes = options?.handleScaylaError?.(error as BaseError);
    if (typeof customScaylaHandlerRes === "string") {
      text = customScaylaHandlerRes;
    } else if (customScaylaHandlerRes === false) {
      return;
    } else {
      text = error.message as string;
    }
  }

  showToast(text, "error");
};

export const showToastForErrorRef = (error: Ref<unknown>, options?: ToastOptions): void => {
  watch(error, () => {
    showToastForError(error.value, options);
  });
};

export function showToastForSuccess(message: Ref<string> | string): void {
  const text = resolveMessage(message);
  if (text) {
    showToast(text, "success");
  }
}

export function showToastForInfo(message: Ref<string> | string): void {
  const text = resolveMessage(message);
  if (text) {
    showToast(text, "info");
  }
}

export function showToastForWarning(message: Ref<string> | string): void {
  const text = resolveMessage(message);
  if (text) {
    showToast(text, "warning");
  }
}
